/* ------------------------------------------------------------------ */
/*  Apply clinical-extraction results into the existing synced tables   */
/*  (PRD.271), mirroring how the dermatology analyse flow surfaces its  */
/*  AI output (DermatologyAiAnalyze.tsx applyAiSymptoms / applyAiDiag): */
/*                                                                      */
/*    symptoms  → patient_symptom_entries      (source='ai' badge)      */
/*    diagnoses → patient_diagnosis_entries    (chip)                   */
/*              + patient_diagnosis_suggestions (status='accepted',      */
/*                linked via accepted_diagnosis_entry_id → AI badge)     */
/*                                                                      */
/*  Reusing these already-synced, already-permissioned tables means the */
/*  results render in the existing Symptoms / Diagnoses sections without */
/*  waiting on a new table's sync rules or RBAC seeding. Writes flow     */
/*  through PowerSync → SyncWrite → Postgres. Dedupe is case-insensitive */
/*  against the encounter's existing labels so re-running is idempotent. */
/* ------------------------------------------------------------------ */

interface PowerSyncExec {
  execute(sql: string, params?: unknown[]): Promise<unknown>;
  getAll<T = unknown>(sql: string, params?: unknown[]): Promise<T[]>;
}

export interface ExtractionEchoContext {
  orgId: string;
  clinicId: string | null;
  patientId: string;
  encounterId: string;
  userId: string | null;
  /** The visit_clinical_extractions.id — recorded as the suggestion's run id. */
  extractionId: string;
  /** Provenance tag stored on the suggestion (e.g. 'siksha'). */
  provider: string;
}

/** Insert AI-extracted symptom chips (source='ai'), skipping existing labels. */
export async function applyExtractionSymptoms(
  db: PowerSyncExec,
  symptoms: readonly string[],
  ctx: ExtractionEchoContext,
): Promise<void> {
  if (symptoms.length === 0) return;
  const existing = await existingLabels(db, 'patient_symptom_entries', ctx.encounterId);
  const now = new Date().toISOString();
  for (const raw of symptoms) {
    const label = raw.trim();
    if (!label || existing.has(label.toLowerCase())) continue;
    try {
      await db.execute(
        `INSERT INTO patient_symptom_entries
          (id, org_id, clinic_id, patient_id, encounter_id, label, source, created_at, updated_at, updated_by)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [crypto.randomUUID(), ctx.orgId, ctx.clinicId, ctx.patientId, ctx.encounterId, label, 'ai', now, now, ctx.userId],
      );
      existing.add(label.toLowerCase());
    } catch (err) {
      console.warn('[clinical-extraction] apply symptom failed:', err);
    }
  }
}

/**
 * Insert AI-extracted diagnoses as chips with an "AI Suggested" badge, mirroring
 * the analyse flow (PRD.217). Each writes a `patient_diagnosis_entries` row plus
 * an accepted `patient_diagnosis_suggestions` row linked via
 * `accepted_diagnosis_entry_id` (what the Diagnoses tab reads to show the badge).
 * The chip is removable with one click — the clinician's review/undo affordance.
 */
export async function applyExtractionDiagnoses(
  db: PowerSyncExec,
  diagnoses: readonly string[],
  ctx: ExtractionEchoContext,
): Promise<void> {
  if (diagnoses.length === 0) return;
  const existing = await existingLabels(db, 'patient_diagnosis_entries', ctx.encounterId);
  const now = new Date().toISOString();
  for (const raw of diagnoses) {
    const label = raw.trim();
    if (!label || existing.has(label.toLowerCase())) continue;
    const entryId = crypto.randomUUID();
    try {
      await db.execute(
        `INSERT INTO patient_diagnosis_entries
          (id, org_id, clinic_id, patient_id, encounter_id, label, created_at, updated_at, updated_by)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [entryId, ctx.orgId, ctx.clinicId, ctx.patientId, ctx.encounterId, label, now, now, ctx.userId],
      );
      await db.execute(
        `INSERT OR REPLACE INTO patient_diagnosis_suggestions
          (id, org_id, clinic_id, patient_id, encounter_id,
           ai_analysis_run_id, source_provider, diagnosis_text,
           condition, severity, confidence, status,
           accepted_label, accepted_diagnosis_entry_id, accepted_by, accepted_at,
           created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          crypto.randomUUID(),
          ctx.orgId,
          ctx.clinicId,
          ctx.patientId,
          ctx.encounterId,
          ctx.extractionId,
          ctx.provider,
          label,
          null,
          null,
          null,
          'accepted',
          label,
          entryId,
          ctx.userId,
          now,
          now,
          now,
        ],
      );
      existing.add(label.toLowerCase());
    } catch (err) {
      console.warn('[clinical-extraction] apply diagnosis failed:', err);
    }
  }
}

async function existingLabels(
  db: PowerSyncExec,
  table: 'patient_symptom_entries' | 'patient_diagnosis_entries',
  encounterId: string,
): Promise<Set<string>> {
  try {
    const rows = await db.getAll<{ label: string | null }>(
      `SELECT label FROM ${table} WHERE encounter_id = ?`,
      [encounterId],
    );
    return new Set(rows.map((r) => (r.label ?? '').toLowerCase()).filter(Boolean));
  } catch {
    return new Set();
  }
}
